import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LocationProvider } from './contexts/LocationContext';
import { ResumeProvider, setSessionRevalidator, setSessionInvalidator } from './contexts/ResumeContext';
import { recordApiError as _diagRecordApiError, recordRevalidation as _diagRecordRevalidation, recordSessionDurability as _diagRecordSessionDurability, setAuthGateGetter as _diagSetAuthGateGetter } from './utils/diagnostics';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import CustomerLandingPage from './pages/CustomerLandingPage';
import VendorLandingPage from './pages/VendorLandingPage';
import AuthPage from './pages/AuthPage';
import AuthErrorBoundary from './components/AuthErrorBoundary';
import GlobalErrorBoundary from './components/GlobalErrorBoundary';
import ForgotPassword from './pages/ForgotPassword';
import Contact from './pages/Contact';
import FAQsPage from './pages/FAQsPage';
import FeedbackPage from './pages/FeedbackPage';
import MyFeedbackPage from './pages/MyFeedbackPage';
import VendorDashboard from './pages/VendorDashboard';
import CustomerDashboard from './pages/CustomerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import PlatformSettings from './pages/PlatformSettings';
import BrowseVendors from './pages/BrowseVendors';
import VendorDetail from './pages/VendorDetail';
import VendorGuidePage from './pages/VendorGuidePage';
import BecomeProvider from './pages/BecomeProvider';
import PlanEvent from './pages/PlanEvent';
import ConciergeIntakePage from './pages/ConciergeIntakePage';
import ConciergeSubmittedPage from './pages/ConciergeSubmittedPage';
import EventDetail from './pages/EventDetail';
import BriefViewerPage from './pages/BriefViewerPage';
import ConciergeAdminPage from './pages/admin/ConciergeAdminPage';
import CaseWorkspacePage from './pages/admin/CaseWorkspacePage';
// EventDashboard merged into EventDetail
import FoundingProvidersPage from './pages/FoundingProvidersPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import NotificationSettingsPage from './components/NotificationSettingsPage';
import PushNotificationHandler from './components/PushNotificationHandler';
import NavigationSetter from './components/NavigationSetter';
import DeleteAccount from './pages/DeleteAccount';
import DiagnosticsPage from './pages/DiagnosticsPage';
import { Toaster, toast } from './components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './components/ui/alert-dialog';
import pushNotificationService from './services/pushNotifications';
import { CeleyLoader } from './components/ui/CeleyLoader';
import {
  isNative as isNativePlatform,
  getRefreshToken as getNativeRefreshToken,
  setRefreshToken as setNativeRefreshToken,
  clearRefreshToken as clearNativeRefreshToken,
  primeRefreshTokenCache as primeNativeRefreshTokenCache,
} from './utils/nativeTokenStore';

// Native iOS/Android session persistence (Feb 2026). On Capacitor builds we
// send X-Client-Platform: native so the backend returns the refresh token
// in the response body and also accepts it via Authorization: Bearer. Web
// continues to use the existing httpOnly refresh-token cookie unchanged.
// Native refresh-token read with a hard timeout. iOS Capacitor Preferences
// IPC can stall indefinitely (root cause of "RESUME_REVALIDATE_TIMEOUT"
// loops). 1.5s is generous — local KV reads complete in <50ms on a healthy
// device. Treats timeout as no-token so refresh fails cleanly into the
// controlled re-auth path instead of hanging.
const NATIVE_TOKEN_READ_TIMEOUT_MS = 1500;
const _readNativeRefreshTokenSafely = async (source = 'auth-gate-refresh') => {
  if (!isNativePlatform()) return null;
  return Promise.race([
    getNativeRefreshToken(source).catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve('__NATIVE_TOKEN_READ_TIMEOUT__'), NATIVE_TOKEN_READ_TIMEOUT_MS)),
  ]);
};

const buildNativeAuthHeaders = async (extra = {}) => {
  if (!isNativePlatform()) return extra;
  const headers = { ...extra, 'X-Client-Platform': 'native' };
  const stored = await _readNativeRefreshTokenSafely();
  if (stored === '__NATIVE_TOKEN_READ_TIMEOUT__') {
    // Surface the specific failure mode so Resume Engine + diagnostics can
    // classify it correctly (vs. lumping under generic 'network-fail').
    const e = new Error('NATIVE_TOKEN_READ_TIMEOUT');
    e.code = 'NATIVE_TOKEN_READ_TIMEOUT';
    throw e;
  }
  if (stored) headers.Authorization = `Bearer ${stored}`;
  return headers;
};
import ImpersonationBanner from './components/ImpersonationBanner';
import { navigateTo } from './services/navigationService';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { useKeyboardAvoidance } from './hooks/useKeyboardAvoidance';
import { OnboardingProvider } from './context/OnboardingContext';
import { PrimaryFocusProvider } from './context/PrimaryFocusContext';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Feature flag for role switching
// Enabled by default. Set REACT_APP_ENABLE_ROLE_SWITCH=false to explicitly disable.
const ENABLE_ROLE_SWITCH = process.env.REACT_APP_ENABLE_ROLE_SWITCH !== 'false';

// Protected flows where role switching should be disabled
const PROTECTED_FLOW_PATTERNS = [
  '/checkout',
  '/booking/.*/payment',
  '/listings/.*/edit',
  '/proposals/.*/edit',
  '/onboarding',
  '/become-provider'
];

// Token refresh state
let isRefreshing = false;
let refreshSubscribers = [];

// Subscribe to token refresh completion
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers when token is refreshed.
// token === null signals refresh failure → subscribers should reject
// with the original 401 error rather than wait forever. Audit 2026-02-28.
const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Feb 2026: session epoch / generation counter (Fix A).
// Incremented synchronously by logout() before any await. Every async
// auth-mutating path (attemptTokenRefresh, checkAuth, 401 interceptor)
// captures the epoch on entry and refuses to commit writes (localStorage,
// nativeTokenStore, setUser) when its captured value no longer matches
// the live counter. This makes post-logout refreshes a silent no-op so
// a stale in-flight call cannot un-do an intentional logout.
let sessionEpoch = 0;
const captureEpoch = () => sessionEpoch;
const epochValid = (captured) => captured === sessionEpoch;

// Attempt to refresh the access token.
// Throws an Error with .code set to one of:
//   NATIVE_TOKEN_READ_TIMEOUT  — Capacitor Preferences hung; treat as terminal
//   REFRESH_TOKEN_MISSING      — native build but no stored refresh token
//   REFRESH_REQUEST_TIMEOUT    — /auth/refresh axios timeout (12s)
//   REFRESH_REQUEST_401        — /auth/refresh returned 401/403 (revoked)
//   SESSION_EPOCH_MISMATCH     — logout happened while in flight
//   NETWORK_FAIL               — other network error (offline, DNS, etc.)
const attemptTokenRefresh = async () => {
  const epoch = captureEpoch();
  let headers;
  try {
    headers = await buildNativeAuthHeaders();
  } catch (err) {
    // buildNativeAuthHeaders only throws on NATIVE_TOKEN_READ_TIMEOUT.
    throw err;
  }
  // On native, if no Authorization header was attached, the refresh token
  // is genuinely missing (vs. timed out). Surface that specifically so
  // diagnostics can show 'refresh-token-missing'.
  if (isNativePlatform() && !headers.Authorization) {
    const e = new Error('REFRESH_TOKEN_MISSING');
    e.code = 'REFRESH_TOKEN_MISSING';
    throw e;
  }

  let response;
  try {
    response = await axios.post(`${API}/auth/refresh`, {}, {
      withCredentials: true,
      headers,
      timeout: 12000,
    });
  } catch (err) {
    // Classify the failure for Resume Engine + diagnostics.
    if (err?.code === 'ECONNABORTED') {
      const e = new Error('REFRESH_REQUEST_TIMEOUT');
      e.code = 'REFRESH_REQUEST_TIMEOUT';
      throw e;
    }
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      const e = new Error('REFRESH_REQUEST_401');
      e.code = 'REFRESH_REQUEST_401';
      e.response = err.response;
      throw e;
    }
    if (!err?.response) {
      const e = new Error('NETWORK_FAIL');
      e.code = 'NETWORK_FAIL';
      throw e;
    }
    throw err;
  }

  const { token, user, refresh_token: newRefreshToken } = response.data;
  if (!epochValid(epoch)) {
    throw new Error('SESSION_EPOCH_MISMATCH');
  }
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  if (isNativePlatform() && newRefreshToken) {
    const rotResult = await setNativeRefreshToken(newRefreshToken, 'refresh-rotation');
    try {
      _diagRecordSessionDurability({
        durability: rotResult?.ok ? 'durable' : 'volatile',
        reason: rotResult?.outcome || 'unknown',
      });
    } catch { /* noop */ }
  }
  // Refresh succeeded — any prior "session expired" lockout should clear.
  try { clearSessionExpired(); } catch { /* defined below — circular ok at runtime */ }
  return token;
};

// ─── Global Auth Gate (request interceptor) ──────────────────────────────
// Single-flight refresh + 401-storm prevention. Sits in front of every
// axios request the app makes. Contract:
//   - Public endpoints (auth/login, auth/refresh, auth/register, etc.)
//     pass through untouched.
//   - Anonymous calls (no Authorization header anywhere) pass through.
//   - If `sessionExpiredFlag` is true, REJECT every protected request
//     locally with code=SESSION_EXPIRED — no network traffic. Routes the
//     user to /auth one time.
//   - If the local access token is expired or expires within 60s, GATE
//     the request: await a single-flight `pendingRefreshPromise`. All
//     concurrent callers share the same in-flight refresh.
//   - On refresh success: rewrite request Authorization header with the
//     new token and proceed.
//   - On refresh failure: set sessionExpiredFlag, reject every queued
//     caller with the classified error code, navigate to /auth once.
//
// Side benefits:
//   - Eliminates the 401-storm that 8+ dashboard widgets cause on resume.
//   - Decouples the Resume Engine's revalidation from the same path.
//   - Diagnostics can read sessionExpiredFlag / pendingRefreshPromise.
//
// Audit: 2026-02-29 (Global Auth Gate hotfix).
const TOKEN_NEAR_EXPIRY_BUFFER_MS = 60 * 1000;
const AUTH_PATH_ALLOWLIST = /\/api\/auth\/(login|register|refresh|logout|verify-email|reset-password|forgot-password)/i;

let sessionExpiredFlag = false;
let sessionExpiredLastReason = null;
let pendingRefreshPromise = null;
let pendingRefreshLastError = null;

const _decodeTokenExpMs = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    if (typeof payload?.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch { return null; }
};

const _tokenIsExpiredOrExpiring = () => {
  const expMs = _decodeTokenExpMs();
  if (!expMs) return false; // no token / unparseable → don't gate, let backend 401
  return Date.now() > (expMs - TOKEN_NEAR_EXPIRY_BUFFER_MS);
};

const _isProtectedRequest = (config) => {
  const url = config?.url || '';
  if (AUTH_PATH_ALLOWLIST.test(url)) return false;
  const reqAuth = config?.headers?.Authorization || config?.headers?.authorization;
  const defAuth = axios.defaults.headers?.common?.Authorization;
  return !!(reqAuth || defAuth);
};

const _getOrCreateRefreshPromise = () => {
  if (pendingRefreshPromise) return pendingRefreshPromise;
  pendingRefreshPromise = (async () => {
    try {
      const token = await attemptTokenRefresh();
      pendingRefreshLastError = null;
      return token;
    } catch (err) {
      pendingRefreshLastError = err;
      throw err;
    } finally {
      // Allow another refresh attempt on the next tick — but only AFTER
      // the current burst of concurrent callers has settled.
      setTimeout(() => { pendingRefreshPromise = null; }, 0);
    }
  })();
  return pendingRefreshPromise;
};

const _enterSessionExpired = (reason) => {
  if (sessionExpiredFlag) return;
  sessionExpiredFlag = true;
  sessionExpiredLastReason = reason || 'unknown';
  // Tell diagnostics + Resume Engine immediately.
  try {
    _diagRecordRevalidation({
      outcome: _outcomeFromCode(reason),
      errorMessage: reason || null,
    });
  } catch { /* noop */ }
  // Navigate to /auth once, off the interceptor stack.
  setTimeout(() => {
    try {
      const path = window.location.pathname;
      if (!path.includes('/auth') && !path.includes('/diagnostics')) {
        toast.error('Your session expired. Please sign in again.');
        navigateTo('/auth');
      }
    } catch { /* noop */ }
  }, 0);
};

// Public: clear the flag after a successful login / refresh.
export const clearSessionExpired = () => {
  sessionExpiredFlag = false;
  sessionExpiredLastReason = null;
  pendingRefreshLastError = null;
};

// Public: diagnostic read-only snapshot of gate state.
export const getAuthGateState = () => ({
  sessionExpiredFlag,
  sessionExpiredLastReason,
  refreshInFlight: !!pendingRefreshPromise,
  lastRefreshError: pendingRefreshLastError?.code || pendingRefreshLastError?.message || null,
});

const _outcomeFromCode = (code) => {
  switch (code) {
    case 'REFRESH_TOKEN_MISSING':       return 'refresh-token-missing';
    case 'NATIVE_TOKEN_READ_TIMEOUT':   return 'native-token-read-timeout';
    case 'REFRESH_REQUEST_TIMEOUT':     return 'refresh-request-timeout';
    case 'REFRESH_REQUEST_401':         return 'refresh-request-401';
    case 'NETWORK_FAIL':                return 'network-fail';
    case 'SESSION_EPOCH_MISMATCH':      return 'forced-reauth';
    case 'SESSION_EXPIRED':             return 'forced-reauth';
    default:                            return code || 'unknown';
  }
};

// Register the gate. Idempotent — module-init only.
axios.interceptors.request.use(async (config) => {
  if (!_isProtectedRequest(config)) return config;

  if (sessionExpiredFlag) {
    // Suppress the network call AND log it to diagnostics so storm-class
    // bursts (8+ widgets retrying simultaneously) are visible.
    try {
      _diagRecordApiError({
        method: (config.method || '').toUpperCase(),
        url: config.url || '',
        status: null,
        code: 'SESSION_EXPIRED',
        message: `Suppressed by Global Auth Gate (${sessionExpiredLastReason || 'expired'})`,
      });
    } catch { /* noop */ }
    const e = new Error('SESSION_EXPIRED');
    e.code = 'SESSION_EXPIRED';
    throw e;
  }

  if (_tokenIsExpiredOrExpiring()) {
    try {
      const newToken = await _getOrCreateRefreshPromise();
      if (newToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
      }
    } catch (err) {
      _enterSessionExpired(err?.code || err?.message || 'REFRESH_FAILED');
      throw err;
    }
  }
  return config;
});

// ─── End Global Auth Gate ─────────────────────────────────────────────────

// Resume Engine wiring (module init): register the module-scoped refresh
// helper so the Resume Engine can proactively revalidate the access token
// on long-idle foreground transitions BEFORE subscribers refetch. Avoids
// the 8-fetch 401-storm race. The logout-style invalidator is registered
// later from inside the App component because logout() closes over
// setUser / setActiveRoleState. See audit 2026-02-29 (Long-Idle Session
// Revalidation).
setSessionRevalidator(attemptTokenRefresh);

// Boot-time pre-warm of the native refresh token cache. The very first
// Capacitor Preferences IPC read on iOS can take 1-2s after cold-start;
// pre-warming at module-init means the cache is hot before the user's
// first long-idle resume tries to refresh. Fire-and-forget — never
// throws. See native-token-store-and-resume-recording-fix-0531.
try { primeNativeRefreshTokenCache(); } catch { /* noop */ }

// Diagnostics integration: let the diagnostics panel show the current
// Global Auth Gate state (sessionExpiredFlag, refresh in-flight, last
// failure code). Pure getter, no side effects.
try { _diagSetAuthGateGetter(getAuthGateState); } catch { /* noop */ }

// Hang-guard: ensure auth bootstrap can never leave the user stuck on the
// "Loading…" gate, even if axios somehow does not honour its timeout
// (e.g., WKWebView pre-warmup quirks). Wraps a promise in a race against
// a hard timeout; the rejection is shaped like a normal Auth error so the
// existing catch + cachedUser fallback paths in checkAuth handle it.
const withHangGuard = (promise, ms = 15000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => {
      const err = new Error('AUTH_BOOTSTRAP_TIMEOUT');
      err.code = 'ECONNABORTED';
      reject(err);
    }, ms)),
  ]);
};

// Set up global axios interceptor for 401 handling with refresh
let interceptorId = null;
let isHandling401 = false;

const setupAxiosInterceptor = (logoutCallback) => {
  // Remove existing interceptor if any
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }
  
  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      // Only handle 401 for authenticated requests (not login/refresh itself)
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Don't retry refresh endpoint or login endpoint
        if (originalRequest.url?.includes('/auth/refresh') || 
            originalRequest.url?.includes('/auth/login')) {
          return Promise.reject(error);
        }

        // IMPERSONATION GUARD: if an admin is acting-as another user, the
        // refresh cookie still belongs to the admin. Attempting a refresh
        // here would silently replace the impersonation token with the
        // admin's token (identity drift). Skip refresh entirely and bubble
        // the 401 to the caller without touching stored auth. Exit
        // impersonation manually via the banner to restore the admin.
        if (localStorage.getItem('impersonation')) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        
        // Fix A: capture the session epoch BEFORE refreshing. If the user
        // logs out while this refresh is in flight, the captured value
        // will no longer match the live counter and we must NOT retry
        // the original request (which would write fresh tokens via
        // attemptTokenRefresh and reauthorize a logged-out request).
        const interceptorEpoch = captureEpoch();

        // If already refreshing, wait for it to complete
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeTokenRefresh((token) => {
              // Refresh failed (null signal from onTokenRefreshed) → reject
              // with the original 401 instead of hanging on a never-arriving
              // retry token. See audit 2026-02-28.
              if (!token) { resolve(Promise.reject(error)); return; }
              if (!epochValid(interceptorEpoch)) {
                resolve(Promise.reject(error));
                return;
              }
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(axios(originalRequest));
            });
          });
        }
        
        isRefreshing = true;
        
        try {
          const newToken = await attemptTokenRefresh();
          isRefreshing = false;
          if (!epochValid(interceptorEpoch)) {
            // Session was invalidated mid-refresh. attemptTokenRefresh
            // itself also bails on epoch mismatch (and throws), so we
            // should not normally reach this branch — but the guard
            // here is defense-in-depth in case the throw is missed.
            return Promise.reject(error);
          }
          onTokenRefreshed(newToken);
          
          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          // Release any queued 401 subscribers — they should reject with
          // the original error rather than wait forever. token=null is
          // the signal subscribers check for. See audit 2026-02-28.
          onTokenRefreshed(null);

          // Refresh failed - clear auth and redirect to login
          if (!isHandling401) {
            isHandling401 = true;
            
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('activeRole');
            
            if (logoutCallback) {
              logoutCallback();
            }
            
            if (!window.location.pathname.includes('/auth') &&
                !window.location.pathname.includes('/diagnostics')) {
              toast.error('Your session expired. Please sign in again.');
              setTimeout(() => {
                // Use navigation service for proper React Router navigation
                navigateTo('/auth');
              }, 100);
            }
            
            setTimeout(() => {
              isHandling401 = false;
            }, 2000);
          }
          
          return Promise.reject(refreshError);
        }
      }
      
      return Promise.reject(error);
    }
  );
};

// Auth Context
export const AuthContext = React.createContext(null);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Axios request interceptor: inject impersonation headers when active
let requestInterceptorId = null;
const setupImpersonationInterceptor = () => {  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
  }
  requestInterceptorId = axios.interceptors.request.use((config) => {
    const impersonation = localStorage.getItem('impersonation');
    if (impersonation) {
      try {
        const data = JSON.parse(impersonation);
        config.headers['X-Impersonation-Admin-Id'] = data.adminUser?.user_id || data.adminUser?.id || '';
        config.headers['X-Impersonation-Admin-Email'] = data.adminUser?.email || '';
        config.headers['X-Impersonation-Target-Id'] = data.targetUserId || '';
        config.headers['X-Impersonation-Target-Email'] = data.targetEmail || '';
      } catch {}
    }
    return config;
  });
};
setupImpersonationInterceptor();

// ─── Diagnostics: global axios error logger ───────────────────────────
// READ-ONLY observation interceptor. Records every failed response into
// the in-memory diagnostics ring buffer so the TestFlight diagnostics
// panel can show what's actually failing. Does NOT mutate the request,
// does NOT swallow errors, does NOT retry. Safe to remove pre App Store
// by deleting this block and src/utils/diagnostics.js.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      _diagRecordApiError({
        method: (error.config?.method || '').toUpperCase(),
        url: error.config?.url || '',
        status: error.response?.status,
        code: error.code,
        message: error.response?.data?.detail
          || error.response?.data?.message
          || error.message
          || 'unknown',
      });
    } catch (_) { /* noop — diagnostics must never break the app */ }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole, adminOnly }) => {
  const { user, loading, activeRole, hasRole, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // RoleSwitchGuard: when a dual-role user visits a route that belongs to the
  // opposite role family, render a small confirmation instead of silently
  // flipping activeRole. Single-role users and admins bypass the prompt.
  const needsRoleConfirmation = Boolean(
    ENABLE_ROLE_SWITCH &&
      user &&
      !user.is_admin &&
      requiredRole &&
      requiredRole !== 'admin' &&
      hasRole(requiredRole) &&
      activeRole &&
      activeRole !== requiredRole &&
      // user must also have their CURRENT role — i.e. be legitimately dual-role.
      hasRole(activeRole)
  );

  const handleConfirmSwitch = () => {
    setActiveRole(requiredRole, { silent: true });
    // Dialog unmounts; children render in the new role.
  };

  const handleCancelSwitch = () => {
    // Send the user back to the dashboard that matches their CURRENT intent.
    const fallback = activeRole === 'vendor' ? '/vendor/dashboard' : '/customer-dashboard';
    // Avoid navigating to the same path we're on (no-op would leave dialog open).
    if (location.pathname !== fallback) {
      navigate(fallback, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  if (loading) {
    return <CeleyLoader testid="app-bootstrap-loader" />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Feb 2026 zombie-dashboard guard: if React has a stale `user` object
  // but localStorage no longer has a usable token, every protected fetch
  // will 401 and the dashboard will render an empty shell. Refuse to
  // render — force the user to /auth instead. This is the visible
  // "logout pressed but UI stays" failure mode on native iOS.
  if (!localStorage.getItem('token')) {
    try { console.warn('[ProtectedRoute] user truthy but no token → forcing /auth'); } catch (_) { /* noop */ }
    return <Navigate to="/auth" replace />;
  }

  // Admin-only routes
  if (adminOnly && !user.is_admin && !hasRole('admin')) {
    return <Navigate to="/" replace />;
  }

  // Role-based routes - check roles array (new) or fall back to role (legacy)
  if (requiredRole) {
    const userRoles = user.roles || [user.role];
    const isAdmin = user.is_admin || userRoles.includes('admin');

    // Fix D: when a user lacks the required role, redirect to their OWN
    // role's dashboard directly instead of bouncing through '/' (which
    // CustomerLandingPage then redirects again — vendor → /vendor/dashboard,
    // creating an apparent freeze when the user is already on vendor side
    // and taps a customer-only surface like /customer-dashboard?tab=events).
    if (!isAdmin && !userRoles.includes(requiredRole)) {
      const ownDashboard = userRoles.includes('vendor')
        ? '/vendor/dashboard'
        : userRoles.includes('customer')
          ? '/customer-dashboard'
          : '/';
      // Guard: never Navigate to the same path we're already on (no-op +
      // would create a render loop). Fall through to '/' only as last resort.
      const safeTarget = location.pathname === ownDashboard ? '/' : ownDashboard;
      return <Navigate to={safeTarget} replace />;
    }
  }

  // Cross-role confirmation overlay: shown above a neutral scaffold so there
  // is no flash of the "wrong mode" dashboard underneath while the user
  // decides. Small, premium, single confirm + single cancel.
  if (needsRoleConfirmation) {
    const targetLabel = requiredRole === 'vendor' ? 'Provider' : 'Customer';
    const currentLabel = activeRole === 'vendor' ? 'Provider' : 'Customer';
    return (
      <>
        <div className="min-h-screen bg-gray-50" aria-hidden="true" />
        <AlertDialog open>
          <AlertDialogContent className="max-w-sm" data-testid="role-switch-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Switch to {targetLabel} mode?</AlertDialogTitle>
              <AlertDialogDescription>
                You're opening a {targetLabel} area while in {currentLabel} mode.
                Switch to {targetLabel} mode to continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={handleCancelSwitch}
                data-testid="role-switch-confirm-cancel"
              >
                Stay in {currentLabel}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmSwitch}
                data-testid="role-switch-confirm-accept"
                className={requiredRole === 'vendor' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                Switch to {targetLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  return children;
};

// Redirect old EventDashboard URLs to unified EventDetail
const RedirectToDashboard = () => {
  const loc = useLocation();
  const eventId = loc.pathname.split('/events/')[1]?.split('/')[0];
  return <Navigate to={`/events/${eventId}`} replace />;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRole, setActiveRoleState] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem('activeRole') || 'customer';
  });

  // Helper to normalize roles from user object (handles both old and new format)
  const normalizeRoles = useCallback((userData) => {
    if (userData?.roles && Array.isArray(userData.roles) && userData.roles.length > 0) {
      return userData.roles;
    }
    // Fall back to single role
    const role = userData?.role || 'customer';
    return role === 'admin' ? ['admin', 'customer'] : [role];
  }, []);

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    if (!user) return false;
    const roles = normalizeRoles(user);
    return roles.includes(role);
  }, [user, normalizeRoles]);

  // Check if user can switch roles (feature flag + has multiple roles)
  const canSwitchRoles = useCallback(() => {
    if (!ENABLE_ROLE_SWITCH || !user) return false;
    const roles = normalizeRoles(user);
    // Can switch if user has both customer and vendor roles
    return roles.includes('customer') && roles.includes('vendor');
  }, [user, normalizeRoles]);

  // Check if role switching is currently blocked (in protected flow)
  const isRoleSwitchBlocked = useCallback((pathname) => {
    return PROTECTED_FLOW_PATTERNS.some(pattern => {
      const regex = new RegExp(`^${pattern.replace(/\*/g, '[^/]+')}$`);
      return regex.test(pathname);
    });
  }, []);

  // Set active role with localStorage persistence
  const setActiveRole = useCallback((role, options = {}) => {
    if (!ENABLE_ROLE_SWITCH) return;
    
    const { silent = false } = options;
    const roles = normalizeRoles(user);
    if (!roles.includes(role)) {
      console.warn(`User doesn't have role: ${role}`);
      return;
    }
    
    setActiveRoleState(role);
    localStorage.setItem('activeRole', role);
    
    // Show toast confirmation only if not silent
    if (!silent) {
      const roleLabel = role === 'vendor' ? 'Provider' : 'Customer';
      toast.success(`Switched to ${roleLabel} mode`);
    }
  }, [user, normalizeRoles]);

  // Define functions first (before useEffect hooks)
  const initializeMobileApp = async () => {
    try {
      // Hide splash screen after app is ready
      await SplashScreen.hide();
      
      // Status bar — match app body background (#F7F7F5) with dark icons.
      // The previous emerald/Light-icons setting left a visible color seam
      // between status bar and content. Role tinting was moved to other
      // ambient cues (avatar ring). See audit 2026-02-28 (Issue 2).
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#F7F7F5' });
      
      // Show the accessory bar (Done/Next buttons above keyboard)
      Keyboard.setAccessoryBarVisible({ isVisible: true });
      
      // Keyboard avoidance is handled globally by useKeyboardAvoidance hook
    } catch (error) {
      // Not a mobile app or feature not available
      console.log('Mobile features not available');
    }
  };

  const initializePushNotifications = async () => {
    try {
      // Admin override: admin users have no in-app toggle UI, so a stored
      // 'disabled' would otherwise permanently block their test-push flow.
      // Override is session-only inside the service — see initialize() docs.
      const userRoles = user?.roles || (user?.role ? [user.role] : []);
      const isAdminUser = !!(user?.is_admin || userRoles.includes('admin'));
      await pushNotificationService.initialize({ forceForAdmin: isAdminUser });
      if (user && localStorage.getItem('token')) {
        console.log('PUSH_DEBUG: Session restore detected — re-registering token');
        await pushNotificationService.reRegisterToken();
      }
      // Re-register on session restore after auth/user are available.
      // Fresh login also calls reRegisterToken() in login().
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
    }
  };

  const checkAuth = async () => {
    const epoch = captureEpoch();
    const token = localStorage.getItem('token');
    
    if (!token) {
      // No token - try to refresh using cookie (silent refresh)
      try {
        // Hang-guard: cap the refresh attempt so a stalled network on
        // cold-start cannot freeze the Loading… gate indefinitely.
        const newToken = await withHangGuard(attemptTokenRefresh(), 15000);
        // Fix A: bail if session was invalidated mid-flight.
        if (!epochValid(epoch)) { setLoading(false); return; }
        // Fix A: fetch fresh /auth/me after a successful silent refresh.
        // This heals stale cached `user` objects (e.g., legacy vendors whose
        // `roles[]` is missing 'customer' from a pre-normalization session).
        try {
          const me = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${newToken || localStorage.getItem('token')}` },
            timeout: 10000,
          });
          if (!epochValid(epoch)) { setLoading(false); return; }
          setUser(me.data);
          localStorage.setItem('user', JSON.stringify(me.data));
        } catch (meErr) {
          if (!epochValid(epoch)) { setLoading(false); return; }
          // Fallback: use cached user if /auth/me unreachable (offline cold start)
          // Fix E: only restore cached user if a usable token is also
          // present. Otherwise we'd render a "zombie" authenticated shell
          // (greeting from cached user.name) while every protected fetch
          // 401s — see audit Section 6.
          const cachedUser = localStorage.getItem('user');
          if (cachedUser && localStorage.getItem('token')) {
            try { setUser(JSON.parse(cachedUser)); } catch (e) { /* unparseable */ }
          }
        }
        setLoading(false);
        return;
      } catch (e) {
        // No valid refresh token - user needs to login
        setLoading(false);
        return;
      }
    }
    
    // Basic token validation - check if it looks like a JWT
    const isValidJWT = token.split('.').length === 3;
    if (!isValidJWT) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('activeRole');
      setLoading(false);
      return;
    }
    
    // Check if token is expired by decoding it
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
      
      if (now > expiry - bufferTime) {
        // Token expired or about to expire - attempt silent refresh
        try {
          // Hang-guard: same protection as the no-token path so a
          // stalled refresh cannot freeze the Loading… gate. On
          // timeout, we fall through to the existing cached-user
          // fallback below.
          await withHangGuard(attemptTokenRefresh(), 15000);
          if (!epochValid(epoch)) { setLoading(false); return; }
          // Fix A: fetch fresh /auth/me after refresh so cached `user` is
          // never stale-but-authenticated. Mirrors Path 3 behavior.
          try {
            const me = await axios.get(`${API}/auth/me`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              timeout: 10000,
            });
            if (!epochValid(epoch)) { setLoading(false); return; }
            setUser(me.data);
            localStorage.setItem('user', JSON.stringify(me.data));
          } catch (meErr) {
            if (!epochValid(epoch)) { setLoading(false); return; }
            // Fix E: require token presence alongside cached user.
            const cachedUser = localStorage.getItem('user');
            if (cachedUser && localStorage.getItem('token')) {
              try { setUser(JSON.parse(cachedUser)); } catch (e) { /* unparseable */ }
            }
          }
          setLoading(false);
          return;
        } catch (e) {
          // Refresh failed. Distinguish a network/timeout error (keep
          // token + cached user — mirrors the /auth/me network-fail
          // policy below) from a real auth failure (clear and force
          // re-login). The hang-guard rejects with code ECONNABORTED so
          // it is treated as a transient network issue.
          if (!epochValid(epoch)) { setLoading(false); return; }
          const isNetworkOrTimeout = e?.code === 'ECONNABORTED' || !e?.response;
          if (isNetworkOrTimeout) {
            // Fix E: require token presence alongside cached user.
            const cachedUser = localStorage.getItem('user');
            if (cachedUser && localStorage.getItem('token')) {
              try { setUser(JSON.parse(cachedUser)); } catch (parseErr) { /* unparseable */ }
            }
          } else {
            // Real auth failure → clear and route to /auth.
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('activeRole');
          }
          setLoading(false);
          return;
        }
      }
    } catch (e) {
      // Could not decode token expiry - continue with validation
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000 // 10 second timeout
      });
      if (!epochValid(epoch)) { setLoading(false); return; }
      setUser(response.data);
      // Fix A: persist fresh user to localStorage so subsequent cold starts
      // (offline / network-fail Path 1+2) have current role data.
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('[Auth] Auth check failed:', error.response?.status);
      if (!epochValid(epoch)) { setLoading(false); return; }
      
      // On 401, the interceptor will handle refresh automatically
      if (error.response?.status === 401) {
        // Interceptor should have handled this - token refresh failed
      } else if (error.code === 'ECONNABORTED' || !error.response) {
        // Network timeout or server unreachable - keep token, user might be offline
        // Fix E: require token presence alongside cached user.
        const cachedUser = localStorage.getItem('user');
        if (cachedUser && localStorage.getItem('token')) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            // Could not parse cached user
          }
        }
      }
      // Other server errors (5xx) - keep token, might be temporary
    }
    setLoading(false);
  };

  const login = async (userData, token, refreshToken) => {
    // Store token and user data
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData)); // Cache user data for resilience
    setUser(userData);
    // Clear any prior Global Auth Gate session-expired state so protected
    // requests stop short-circuiting. Idempotent.
    try { clearSessionExpired(); } catch { /* noop */ }

    // Native session persistence: when AuthPage receives `refresh_token` in
    // the login response body (because it sent X-Client-Platform: native),
    // persist it via Capacitor Preferences so the next cold start can
    // refresh without depending on WKWebView cross-origin cookies.
    if (isNativePlatform() && refreshToken) {
      try {
        const result = await setNativeRefreshToken(refreshToken, 'login');
        try {
          _diagRecordSessionDurability({
            durability: result?.ok ? 'durable' : 'volatile',
            reason: result?.outcome || 'unknown',
          });
        } catch { /* noop */ }
        if (!result?.ok && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[Auth] Login session is VOLATILE (persistence not verified):', result?.outcome, result?.error);
        }
      } catch (e) {
        try { _diagRecordSessionDurability({ durability: 'volatile', reason: `setRefreshToken-threw: ${e?.message || e}` }); } catch { /* noop */ }
        console.warn('[Auth] Failed to persist native refresh token:', e);
      }
    } else if (!isNativePlatform()) {
      try { _diagRecordSessionDurability({ durability: 'durable', reason: 'web-cookie' }); } catch { /* noop */ }
    }

    // Pull native FCM token (stored in UserDefaults by AppDelegate) and
    // register it with the backend now that the auth token is in localStorage.
    // Guard: only fire if the auth token is actually present and usable —
    // prevents 401s from stale-bootstrap paths.
    // Feb 2026: fire-and-forget so a cold-start backend cannot block the
    // login UX. The internal fetch has its own 15s timeout + graceful
    // failure; if it fails the token will re-register on next session
    // restore via initializePushNotifications().
    if (localStorage.getItem('token')) {
      console.log("AUTH READY — triggering push re-register (non-blocking)");
      pushNotificationService.reRegisterToken().catch((e) => {
        console.warn('[Auth] push reRegisterToken failed (non-blocking):', e);
      });
    }

    // Set initial active role based on user's roles
    if (ENABLE_ROLE_SWITCH) {
      const roles = userData.roles || [userData.role];
      const savedRole = localStorage.getItem('activeRole');

      // Vendor-primary bias: if the authenticated account's signup/primary role
      // is 'vendor', default activeRole to 'vendor' BEFORE honoring a stale
      // savedRole='customer'. This corrects the case where dual-role vendor
      // accounts get stuck in customer mode after a relaunch. Manual switching
      // afterwards still persists normally (setActiveRole writes savedRole).
      const isVendorPrimary = userData.role === 'vendor' && roles.includes('vendor');
      if (isVendorPrimary) {
        setActiveRoleState('vendor');
        localStorage.setItem('activeRole', 'vendor');
      } else if (savedRole && roles.includes(savedRole)) {
        setActiveRoleState(savedRole);
      } else if (roles.includes('vendor')) {
        // Default to vendor if they have vendor role
        setActiveRoleState('vendor');
        localStorage.setItem('activeRole', 'vendor');
      } else {
        setActiveRoleState('customer');
        localStorage.setItem('activeRole', 'customer');
      }
    }
  };

  // Feb 2026 native hotfix: forceLocalLogout is a synchronous, IPC-free
  // escape hatch surfaced as an "Emergency Reset Session" button in
  // AccountPage. Used when the normal Logout flow hangs (e.g., a Capacitor
  // Preferences IPC stall). Wipes every auth surface we can reach without
  // awaiting anything, then forces a hard reload so any cached React /
  // axios state is dropped.
  const forceLocalLogout = () => {
    try { console.warn('[FORCE-LOGOUT] invoked'); } catch (_) { /* noop */ }
    sessionEpoch++;
    try { localStorage.removeItem('token'); } catch (_) { /* noop */ }
    try { localStorage.removeItem('user'); } catch (_) { /* noop */ }
    try { localStorage.removeItem('activeRole'); } catch (_) { /* noop */ }
    try { localStorage.removeItem('eventDraft'); } catch (_) { /* noop */ }
    try { localStorage.removeItem('authRedirect'); } catch (_) { /* noop */ }
    try { delete axios.defaults.headers.common['Authorization']; } catch (_) { /* noop */ }
    setUser(null);
    setActiveRoleState('customer');
    // Best-effort native clear; we don't wait for it.
    if (isNativePlatform()) {
      try { clearNativeRefreshToken('force-local-logout').catch(() => { /* noop */ }); } catch (_) { /* noop */ }
    }
    // Hard reload guarantees a clean WebView state. The router will then
    // resolve to '/' (CustomerLandingPage) because token is gone.
    try {
      window.location.href = '/';
    } catch (_) {
      try { navigateTo('/'); } catch (__) { /* noop */ }
    }
  };

  const logout = async () => {
    // Feb 2026 native hotfix: logout is now SYNCHRONOUS-FIRST. All local
    // cleanup runs before any await so the user is locally signed out
    // the instant the button is tapped, even if Capacitor IPC or network
    // hangs. Native debug markers below survive in Safari Web Inspector.
    try { console.log('[LOGOUT] start', { isNative: isNativePlatform(), epoch: sessionEpoch }); } catch (_) { /* noop */ }

    // 1. Increment session epoch FIRST. Any in-flight refresh / checkAuth
    //    / 401 interceptor captures the OLD epoch and will refuse to
    //    commit further state.
    sessionEpoch++;

    // 2. Synchronous local cleanup — NO awaits in this block.
    try { localStorage.removeItem('token'); } catch (_) { /* noop */ }
    try { localStorage.removeItem('user'); } catch (_) { /* noop */ }
    try { localStorage.removeItem('activeRole'); } catch (_) { /* noop */ }
    try { delete axios.defaults.headers.common['Authorization']; } catch (_) { /* noop */ }
    setUser(null);
    setActiveRoleState('customer');

    // 3. Explicit route reset BEFORE any await.
    try { navigateTo('/'); } catch (_) { /* noop in non-router contexts */ }

    try { console.log('[LOGOUT] sync cleanup done — token/user/role cleared, navigated to /'); } catch (_) { /* noop */ }

    // ── 4. Best-effort REMOTE / NATIVE cleanup below. None of this is
    //       allowed to block or undo the synchronous logout above. We
    //       intentionally do NOT capture the native refresh token before
    //       clearing — server-side revocation is a "nice to have"; the
    //       token has a server-side TTL and is now unusable from this
    //       device anyway.

    if (isNativePlatform()) {
      try {
        await clearNativeRefreshToken('logout');
        try { console.log('[LOGOUT] native refresh token cleared'); } catch (_) { /* noop */ }
      } catch (e) {
        try { console.warn('[LOGOUT] clearNativeRefreshToken failed (non-blocking):', e); } catch (_) { /* noop */ }
      }
    }

    // Call backend logout (best-effort, 5s timeout). No token in the
    // request — server-side revocation will rely on the httpOnly cookie
    // on web. On native, the cookie is unreliable, so this is a no-op
    // for native users; that's acceptable because the local copy is gone.
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: { 'X-Client-Platform': isNativePlatform() ? 'native' : 'web' },
        timeout: 5000,
      });
      try { console.log('[LOGOUT] backend /auth/logout completed'); } catch (_) { /* noop */ }
    } catch (error) {
      try { console.warn('[LOGOUT] backend /auth/logout failed (non-blocking):', error?.response?.status || error?.code || error?.message); } catch (_) { /* noop */ }
    }

    // Push cleanup — fully best-effort.
    try { await pushNotificationService.cleanup(); } catch (_) { /* swallow */ }

    try { console.log('[LOGOUT] complete'); } catch (_) { /* noop */ }
  };

  // useEffect hooks (after function declarations)
  useEffect(() => {
    // Initialize mobile app features
    initializeMobileApp();
    
    // Set up axios interceptor for 401 handling
    setupAxiosInterceptor(() => setUser(null));

    // Resume Engine wiring (component init): register the full logout
    // flow so the Resume Engine can cleanly tear down a zombie session
    // when /auth/refresh returns a genuine 401/403 on long-idle resume.
    // Module-init wiring above only covers the refresh helper, which is
    // module-scoped. logout() closes over setUser etc., so it must be
    // injected from inside the component. Idempotent.
    setSessionInvalidator(logout);

    // Check authentication
    checkAuth();
  }, []);

  // Initialize push notifications when user logs in
  useEffect(() => {
    if (user) {
      initializePushNotifications();
      
      // Sync activeRole with user's available roles on login
      if (ENABLE_ROLE_SWITCH) {
        const roles = normalizeRoles(user);
        const currentActiveRole = localStorage.getItem('activeRole');

        // If saved role is not available, reset to appropriate default
        if (!roles.includes(currentActiveRole)) {
          const newRole = roles.includes('vendor') ? 'vendor' : 'customer';
          setActiveRoleState(newRole);
          localStorage.setItem('activeRole', newRole);
        } else if (
          // One-shot vendor-primary bias for session-restore path: if the
          // primary signup role is 'vendor' AND savedRole='customer' AND we
          // have not yet applied this correction for this install, override
          // to vendor mode. Sentinel ensures this fires exactly once so
          // subsequent manual switches to customer are honored.
          user.role === 'vendor' &&
          currentActiveRole === 'customer' &&
          roles.includes('vendor') &&
          localStorage.getItem('roleBiasV2Applied') !== '1'
        ) {
          setActiveRoleState('vendor');
          localStorage.setItem('activeRole', 'vendor');
          localStorage.setItem('roleBiasV2Applied', '1');
        } else if (localStorage.getItem('roleBiasV2Applied') !== '1') {
          // Mark sentinel for non-vendor-primary users too so the one-shot
          // never re-applies if their primary role changes later.
          localStorage.setItem('roleBiasV2Applied', '1');
        }
      }
    }
  }, [user, normalizeRoles]);

  // Global keyboard avoidance — scrolls focused inputs into view on all pages
  useKeyboardAvoidance();

  // Ambient role cue #1: document title prefix. Subtle signal that appears in
  // browser tabs, browser history, and screen readers — no layout impact.
  useEffect(() => {
    if (!ENABLE_ROLE_SWITCH || !user) {
      document.title = 'Celey';
      return;
    }
    const effectiveRole = user.is_admin ? 'admin' : activeRole;
    const prefix =
      effectiveRole === 'vendor' ? 'Provider'
      : effectiveRole === 'admin' ? 'Admin'
      : 'Customer';
    document.title = `${prefix} · Celey`;
  }, [activeRole, user]);

  // Ambient role cue #2: status-bar tint on native iOS (Capacitor).
  // DISABLED Feb 2026 (audit Issue 2) — role tinting caused a visible color
  // seam between status bar and the warm-grey app body. Role identity is
  // still surfaced via the avatar ring and dashboard accents. Effect kept
  // (rather than removed) to preserve the role-change reactive hook for
  // future ambient cues without re-introducing the import/structure churn.
  useEffect(() => {
    if (!ENABLE_ROLE_SWITCH || !user) return;
    // Intentional no-op. Status bar stays at the app-shell-wide #F7F7F5
    // set in initializeMobileApp().
  }, [activeRole, user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      forceLocalLogout,
      loading,
      // Role switch features
      activeRole: ENABLE_ROLE_SWITCH ? activeRole : (user?.role || 'customer'),
      setActiveRole,
      hasRole,
      canSwitchRoles,
      isRoleSwitchBlocked,
      enableRoleSwitch: ENABLE_ROLE_SWITCH,
      roles: user ? normalizeRoles(user) : []
    }}>
      <LocationProvider>
      <ResumeProvider>
      <OnboardingProvider>
      <PrimaryFocusProvider>
      <div className="App">
        {/* Tailwind class keeper – do not remove. Ensures badge classes are included in production CSS bundle */}
        <div
          className="
            hidden
            bg-gray-50 border-gray-200 text-gray-800 text-emerald-600
            bg-amber-50 border-amber-200 text-amber-700 text-amber-500
            bg-yellow-50 border-yellow-200 text-yellow-700 text-yellow-500
            bg-emerald-50 border-emerald-200 text-emerald-700 text-emerald-500
            bg-blue-50 border-blue-200 text-blue-700 text-blue-500
            bg-purple-50 border-purple-200 text-purple-700 text-purple-500
            bg-rose-50 border-rose-200 text-rose-700 text-rose-500
            from-amber-500 to-yellow-500 border-amber-600 text-amber-100
            shadow-sm bg-gradient-to-r
          "
          aria-hidden="true"
        />
        <BrowserRouter>
          <ImpersonationBanner />
          <NavigationSetter />
          <ScrollToTop />
          <PushNotificationHandler />
          <GlobalErrorBoundary>
          <Routes>
            <Route path="/" element={<CustomerLandingPage />} />
            <Route path="/about" element={<LandingPage />} />
            <Route path="/how-it-works" element={<LandingPage />} />
            <Route path="/auth" element={
              <AuthErrorBoundary>
                <AuthPage />
              </AuthErrorBoundary>
            } />
            {/* /signup route - supports referral links like /signup?ref=CODE */}
            <Route path="/signup" element={
              <AuthErrorBoundary>
                <AuthPage />
              </AuthErrorBoundary>
            } />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<FAQsPage />} />
            <Route path="/feedback" element={<FeedbackPage />} />
            <Route path="/my-feedback" element={
              <ProtectedRoute>
                <MyFeedbackPage />
              </ProtectedRoute>
            } />
            <Route path="/my-feedback/:feedbackId" element={
              <ProtectedRoute>
                <MyFeedbackPage />
              </ProtectedRoute>
            } />
            <Route path="/browse" element={<BrowseVendors />} />
            <Route path="/for-vendors" element={<VendorLandingPage />} />
            <Route path="/vendors/:id" element={<VendorDetail />} />
            <Route path="/vendor-guide" element={<VendorGuidePage />} />
            <Route path="/plan-event" element={<PlanEvent />} />
            {/* Phase 4 — Concierge intake (anonymous). The pages themselves
                check ENABLE_CONCIERGE_FLOW via the availability endpoint and
                redirect home when off, so registering the routes unconditionally
                is safe and reversible. */}
            <Route path="/concierge/intake" element={<ConciergeIntakePage />} />
            <Route path="/concierge/submitted" element={<ConciergeSubmittedPage />} />
            <Route path="/founding-providers" element={<FoundingProvidersPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            {/* TestFlight diagnostics — developer-only surface. Gated on the
                same predicate as the AccountPage card so direct navigation to
                /diagnostics in production renders nothing (App Reviewer hardening).
                The underlying utils/diagnostics.js telemetry stays live. */}
            {(process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENABLE_DIAGNOSTICS_UI === 'true') && (
              <Route path="/diagnostics" element={<DiagnosticsPage />} />
            )}
            <Route path="/events/:eventId" element={<EventDetail />} />
            {/* EventDashboard merged into EventDetail — redirect old URLs */}
            <Route path="/events/:eventId/dashboard" element={<RedirectToDashboard />} />
            <Route 
              path="/settings/notifications" 
              element={
                <ProtectedRoute>
                  <NotificationSettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/account/delete"
              element={
                <ProtectedRoute>
                  <DeleteAccount />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/become-provider" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <BecomeProvider />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/vendor/dashboard" 
              element={
                <ProtectedRoute requiredRole="vendor">
                  <VendorDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/customer-dashboard" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <CustomerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/concierge" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <ConciergeAdminPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/concierge/cases/:event_id"
              element={
                <ProtectedRoute adminOnly={true}>
                  <CaseWorkspacePage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/events/:eventId/brief" 
              element={
                <ProtectedRoute requiredRole="customer">
                  <BriefViewerPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <ProtectedRoute adminOnly={true}>
                  <PlatformSettings />
                </ProtectedRoute>
              } 
            />
          </Routes>
          </GlobalErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
      </PrimaryFocusProvider>
      </OnboardingProvider>
      </ResumeProvider>
      </LocationProvider>
    </AuthContext.Provider>
  );
}

export default App;
